import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Plus, Loader2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { useBolUpload } from "@/hooks/useBolUpload";
import { useTrailerReadinessPhotoUpload } from "@/hooks/useTrailerReadinessPhotoUpload";
import { useTenant } from "@/hooks/useTenant";
import { OrderNotesCard, useOrderNoteCount } from "@/components/orders/OrderNotesCard";
import { ChecklistDocViewer } from "./ChecklistDocViewer";
import { ChecklistRow } from "./ChecklistRow";
import {
  isBolRequiredForDeparture,
  resolveLegVerification,
  type DriverVerifiedEvent,
} from "@/lib/driver-departure-readiness";

interface Props {
  legId: string;
  orderId: string;
  orderUniqueId: string;
  trailerId: string | null | undefined;
  driverName?: string;
  /**
   * When false, the "Verify trailer information" row is hidden and its
   * completion is treated as satisfied. Used to skip the verify step on
   * legs whose pickup is a YMS-tracked location (the AYMS check-in flow
   * already captures the same info there).
   */
  verifyRequired?: boolean;
  onReadinessChange?: (state: {
    verified: boolean;
    trailerPhotoLeftCount: number;
    trailerPhotoRightCount: number;
    licensePlatePhotoCount: number;
    bolCount: number;
    bolRequired: boolean;
    allRequiredComplete: boolean;
    needsService: boolean;
  }) => void;
}

export function ReadinessChecklist({
  legId,
  orderId,
  orderUniqueId,
  trailerId,
  driverName,
  verifyRequired = true,
  onReadinessChange,
}: Props) {
  const navigate = useNavigate();
  const { tenantId } = useTenant();
  const bolInput = useRef<HTMLInputElement>(null);
  const trailerPhotoLeftInput = useRef<HTMLInputElement>(null);
  const trailerPhotoRightInput = useRef<HTMLInputElement>(null);
  const licensePlateInput = useRef<HTMLInputElement>(null);
  const [bolViewerOpen, setBolViewerOpen] = useState(false);
  const [trailerPhotoLeftViewerOpen, setTrailerPhotoLeftViewerOpen] = useState(false);
  const [trailerPhotoRightViewerOpen, setTrailerPhotoRightViewerOpen] = useState(false);
  const [licensePlateViewerOpen, setLicensePlateViewerOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);

  const { uploadFiles: uploadBol, isUploading: bolUploading } = useBolUpload({
    orderId,
    orderUniqueId,
    mode: "driver",
    changedBy: driverName,
  });
  const { uploadFiles: uploadTrailerPhotoLeft, isUploading: trailerPhotoLeftUploading } =
    useTrailerReadinessPhotoUpload({
      trailerIdText: trailerId,
      photoType: "trailer",
      variant: "trailer-left",
      legId,
    });
  const { uploadFiles: uploadTrailerPhotoRight, isUploading: trailerPhotoRightUploading } =
    useTrailerReadinessPhotoUpload({
      trailerIdText: trailerId,
      photoType: "trailer",
      variant: "trailer-right",
      legId,
    });
  const { uploadFiles: uploadLicensePlate, isUploading: licensePlateUploading } =
    useTrailerReadinessPhotoUpload({ trailerIdText: trailerId, photoType: "license_plate", legId });

  // Resolve trailer DB id once — needed for both counts and the viewer.
  const { data: trailerDbId = null } = useQuery({
    queryKey: ["trailer-db-id", trailerId, tenantId],
    enabled: !!trailerId && !!tenantId,
    queryFn: async () => {
      const { data } = await supabase
        .from("trailers")
        .select("id")
        .eq("trailer_id", trailerId!)
        .eq("tenant_id", tenantId!)
        .maybeSingle();
      return data?.id ?? null;
    },
  });

  // Counts
  const { data: bolCount = 0 } = useQuery({
    queryKey: ["bol-images-count", orderId],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("order_bol_images")
        .select("id", { count: "exact", head: true })
        .eq("order_id", orderId);
      if (error) throw error;
      return count ?? 0;
    },
  });

  const { data: trailerPhotoLeftCount = 0 } = useQuery({
    queryKey: ["trailer-event-photos-count", trailerId, "trailer-left"],
    enabled: !!trailerDbId,
    queryFn: async () => {
      const { count, error } = await supabase
        .from("trailer_event_photos" as never)
        .select("id", { count: "exact", head: true })
        .eq("trailer_id", trailerDbId!)
        .eq("photo_type", "trailer" as never)
        .ilike("image_path", "%trailer-left-%");
      if (error) return 0;
      return count ?? 0;
    },
  });

  const { data: trailerPhotoRightCount = 0 } = useQuery({
    queryKey: ["trailer-event-photos-count", trailerId, "trailer-right"],
    enabled: !!trailerDbId,
    queryFn: async () => {
      const { count, error } = await supabase
        .from("trailer_event_photos" as never)
        .select("id", { count: "exact", head: true })
        .eq("trailer_id", trailerDbId!)
        .eq("photo_type", "trailer" as never)
        .ilike("image_path", "%trailer-right-%");
      if (error) return 0;
      return count ?? 0;
    },
  });

  const { data: licensePlatePhotoCount = 0 } = useQuery({
    queryKey: ["trailer-event-photos-count", trailerId, "license_plate"],
    enabled: !!trailerDbId,
    queryFn: async () => {
      const { count, error } = await supabase
        .from("trailer_event_photos" as never)
        .select("id", { count: "exact", head: true })
        .eq("trailer_id", trailerDbId!)
        .eq("photo_type", "license_plate" as never);
      if (error) return 0;
      return count ?? 0;
    },
  });

  const { data: noteCount = 0 } = useOrderNoteCount(orderId);

  // Verified = this leg's newest driver_verified event from the Verify
  // screen (always sets truck_id). Scoped by leg_id so a trailer verified on
  // an earlier job doesn't count (DTP-110609). Photo-upload stubs leave
  // truck_id null and are ignored by resolveLegVerification.
  const { data: verifyEvents = [] } = useQuery({
    queryKey: ["driver-verified", trailerDbId, legId],
    enabled: !!trailerDbId && !!legId && verifyRequired,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("trailer_events")
        .select("leg_id, truck_id, load_status, created_at")
        .eq("trailer_id", trailerDbId!)
        .eq("leg_id" as never, legId as never)
        .eq("event_type", "driver_verified" as never);
      if (error) return [] as DriverVerifiedEvent[];
      return (data ?? []) as unknown as DriverVerifiedEvent[];
    },
  });
  const legVerification = resolveLegVerification(verifyEvents, legId);
  const verifiedRaw = legVerification.verified;

  // Trailer needs_service flag — set by the "What's wrong?" flow in the
  // Verify Trailer screen and in Yard check-in. When true, the depart
  // button is gated regardless of trip type.
  const { data: needsService = false } = useQuery({
    queryKey: ["trailer-needs-service", trailerDbId],
    enabled: !!trailerDbId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("trailers")
        .select("needs_service")
        .eq("id", trailerDbId!)
        .maybeSingle();
      if (error) return false;
      return !!data?.needs_service;
    },
  });
  // When verify isn't required for this leg, treat it as satisfied so it
  // never blocks the checklist / Ready-to-Depart gate.
  const verified = verifyRequired ? verifiedRaw : true;

  const bolRequired = isBolRequiredForDeparture({
    verifyRequired,
    verification: legVerification,
  });

  const allRequiredComplete =
    verified &&
    trailerPhotoLeftCount > 0 &&
    trailerPhotoRightCount > 0 &&
    licensePlatePhotoCount > 0 &&
    (!bolRequired || bolCount > 0);

  // Surface state to parent
  const stateKey = `${verified}|${bolCount}|${trailerPhotoLeftCount}|${trailerPhotoRightCount}|${licensePlatePhotoCount}|${bolRequired}|${allRequiredComplete}|${needsService}`;
  const lastKey = useRef<string | null>(null);
  if (onReadinessChange && lastKey.current !== stateKey) {
    lastKey.current = stateKey;
    queueMicrotask(() =>
      onReadinessChange({
        verified,
        trailerPhotoLeftCount,
        trailerPhotoRightCount,
        licensePlatePhotoCount,
        bolCount,
        bolRequired,
        allRequiredComplete,
        needsService,
      }),
    );
  }

  return (
    <div className="rounded-md border divide-y bg-card overflow-hidden">
      {verifyRequired && (
        <ChecklistRow
          complete={verified}
          label="Verify trailer information"
          onClick={() => navigate(`/my-assignments/${legId}/verify-trailer`)}
          trailing={<ChevronRight className="h-5 w-5 text-muted-foreground" />}
        />
      )}

      <ChecklistRow
        complete={trailerPhotoLeftCount > 0}
        label="Upload Trailer photo (Left side)"
        viewCount={trailerPhotoLeftCount}
        onViewClick={() => setTrailerPhotoLeftViewerOpen(true)}
        onClick={() => trailerPhotoLeftInput.current?.click()}
        trailing={
          trailerPhotoLeftUploading ? (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          ) : (
            <Plus className="h-5 w-5 text-muted-foreground" />
          )
        }
      />
      <input
        ref={trailerPhotoLeftInput}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        className="hidden"
        onChange={(e) => {
          const files = e.target.files;
          if (files && files.length > 0) void uploadTrailerPhotoLeft(files);
          if (trailerPhotoLeftInput.current) trailerPhotoLeftInput.current.value = "";
        }}
      />

      <ChecklistRow
        complete={trailerPhotoRightCount > 0}
        label="Upload Trailer photo (Right side)"
        viewCount={trailerPhotoRightCount}
        onViewClick={() => setTrailerPhotoRightViewerOpen(true)}
        onClick={() => trailerPhotoRightInput.current?.click()}
        trailing={
          trailerPhotoRightUploading ? (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          ) : (
            <Plus className="h-5 w-5 text-muted-foreground" />
          )
        }
      />
      <input
        ref={trailerPhotoRightInput}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        className="hidden"
        onChange={(e) => {
          const files = e.target.files;
          if (files && files.length > 0) void uploadTrailerPhotoRight(files);
          if (trailerPhotoRightInput.current) trailerPhotoRightInput.current.value = "";
        }}
      />

      <ChecklistRow
        complete={licensePlatePhotoCount > 0}
        label="Upload License Plate photo"
        viewCount={licensePlatePhotoCount}
        onViewClick={() => setLicensePlateViewerOpen(true)}
        onClick={() => licensePlateInput.current?.click()}
        trailing={
          licensePlateUploading ? (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          ) : (
            <Plus className="h-5 w-5 text-muted-foreground" />
          )
        }
      />
      <input
        ref={licensePlateInput}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        className="hidden"
        onChange={(e) => {
          const files = e.target.files;
          if (files && files.length > 0) void uploadLicensePlate(files);
          if (licensePlateInput.current) licensePlateInput.current.value = "";
        }}
      />

      <ChecklistRow
        complete={bolCount > 0 || !bolRequired}
        label={
          !bolRequired && bolCount === 0 ? "BOL — not required (empty trailer)" : "Upload BOL"
        }
        viewCount={bolCount}
        onViewClick={() => setBolViewerOpen(true)}
        onClick={() => bolInput.current?.click()}
        trailing={
          bolUploading ? (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          ) : (
            <Plus className="h-5 w-5 text-muted-foreground" />
          )
        }
      />
      <input
        ref={bolInput}
        type="file"
        accept="image/*,.pdf,application/pdf"
        capture="environment"
        multiple
        className="hidden"
        onChange={(e) => {
          const files = e.target.files;
          if (files && files.length > 0) void uploadBol(files);
          if (bolInput.current) bolInput.current.value = "";
        }}
      />

      <ChecklistRow
        complete={false}
        optional
        label="Add notes (optional)"
        viewCount={noteCount}
        onViewClick={() => setNotesOpen(true)}
        onClick={() => setNotesOpen(true)}
        trailing={<Plus className="h-5 w-5 text-muted-foreground" />}
      />

      <ChecklistDocViewer
        open={bolViewerOpen}
        onOpenChange={setBolViewerOpen}
        scopeId={orderId}
        kind="bol"
      />
      <ChecklistDocViewer
        open={trailerPhotoLeftViewerOpen}
        onOpenChange={setTrailerPhotoLeftViewerOpen}
        scopeId={trailerDbId}
        kind="trailer-photo-yard-left"
      />
      <ChecklistDocViewer
        open={trailerPhotoRightViewerOpen}
        onOpenChange={setTrailerPhotoRightViewerOpen}
        scopeId={trailerDbId}
        kind="trailer-photo-yard-right"
      />
      <ChecklistDocViewer
        open={licensePlateViewerOpen}
        onOpenChange={setLicensePlateViewerOpen}
        scopeId={trailerDbId}
        kind="license-plate-photo"
      />

      <Drawer open={notesOpen} onOpenChange={setNotesOpen}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>Notes</DrawerTitle>
          </DrawerHeader>
          <div className="px-4 pb-6 max-h-[70vh] overflow-auto">
            <OrderNotesCard orderId={orderId} compact hideHeader />
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}